// ProductCard.tsx

import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TiStarFullOutline } from 'react-icons/ti';
import { Product } from '../types/types';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import AllModal from './AllModal';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { instance } from '../api/axiosInstance';
import Cookies from 'js-cookie';
import Alert from './Alert';
import useAddToCart from '../hooks/useAddToCart';


import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  token: string | null;
  phone: string;
  referralCode: string;
  roles: string[];
  createdAt: string;
  nickname: string;
  profileImageUrl: string;
  uuid: string;
  _seller: boolean;
  snsPublic: boolean;
}

interface ProductCardProps {
  product: Product;
  formatNumber: (number: number | bigint) => string;
}

export default function ProductCard({
  product,
  formatNumber,
}: ProductCardProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false); // State for Alert

  const handleAddToCart = useAddToCart(); // Initialize the hook

  const navigate = useNavigate();
  const routerLocation = useLocation(); // useLocation 추가 및 이름 변경

  // useRef를 컴포넌트 함수 내부로 이동
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleProfileClick = () => {
    navigate(`/profile/${product.nickname}`); // 해당 유저의 프로필 페이지로 이동
  };

  // State for editing
  const [productName, setProductName] = useState(product.name);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>(
    `${process.env.REACT_APP_API_BASE_URL}${product.image}`
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [randomProducts, setRandomProducts] = useState<Product[]>([]);
  const [isLoadingRandomProducts, setIsLoadingRandomProducts] = useState(false);
  const [hasLoadedRandomProducts, setHasLoadedRandomProducts] = useState(false);

  const fetchRandomProducts = async () => {
    if (hasLoadedRandomProducts) return; // 이미 로드된 경우 다시 호출하지 않음
    
    setIsLoadingRandomProducts(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/products/random?username=${product.nickname}&count=4`
      );
      setRandomProducts(response.data);
      setHasLoadedRandomProducts(true);
    } catch (error) {
      console.error('Failed to fetch random products:', error);
    } finally {
      setIsLoadingRandomProducts(false);
    }
  };

  useEffect(() => {
    // Check if user is admin
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user: User = JSON.parse(userData);
        if (user.roles && user.roles.includes('ADMIN')) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error('사용자 데이터 파싱 오류:', error);
      }
    }
  }, []);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const openModal = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError(null);
    setProductName(product.name);
    setThumbnail(null);
    setThumbnailPreview(
      `${process.env.REACT_APP_API_BASE_URL}${product.image}`
    );
  };

  // Handle product name change
  const handleProductNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setProductName(e.target.value);
  };

  // Handle thumbnail change
  const handleThumbnailChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission using axiosInstance
  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append('name', productName);
    if (thumbnail) {
      formData.append('image', thumbnail);
    }

    try {
      const accessToken = Cookies.get('access_token');
      console.log('access_token:', accessToken);

      if (!accessToken) {
        throw new Error(t('product_card.error.no_token'));
      }

      const response = await instance.put(
        `/auth/products/${product.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        const updatedProduct: Product = response.data;
        console.log('Product updated successfully:', updatedProduct);
        closeModal();
      } else {
        setError(t('product_card.error.update_failed'));
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to update product:', error.response?.data);
        setError(
          error.response?.data.message || t('product_card.error.update_failed')
        );
      } else {
        console.error('Error updating product:', error);
        setError(t('product_card.error.update_error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDiscountNumber = (discount: string): number => {
    const parsed = Number(discount);
    return isNaN(parsed) ? 0 : parsed;
  };

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  // Function to toggle more options dropdown
  const toggleUserDropdown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to document
    setIsMoreOpen(!isMoreOpen); // 드롭다운 상태 글
  };

  const moreOptions = isAdmin ? ['신고하기', '수정하기'] : ['신고하기'];

  const handleOptionClick = (option: string) => {
    setIsMoreOpen(false); // 옵션 클릭 시 드롭다운 닫기
    if (option === '신고하기') {
      navigate(`/report/${product.id}`, {
        state: { backgroundLocation: routerLocation, product },
      });
    } else if (option === '수정하기') {
      setIsModalOpen(true); // 수정 모달 열기
    }
  };

  // 외부 클릭 감지 함수
  const handleClickOutside = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      setIsMoreOpen(false); // 외부 클릭 시 드롭다운 닫기
    }
  };

  // useEffect로 외부 클릭 이벤트 등록 및 정리
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Alert for adding to cart */}
      {showAlert && (
        <Alert
          message="상품이 장바구니에 추가되었습니다."
          onClose={() => setShowAlert(false)}
        />
      )}

      <div className="relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
        {/* 상품 이미지 섹션 */}
        <div 
          onClick={() => navigate(`/products/details/${product.id}`)}
          className="cursor-pointer"
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl">
            <div className="absolute inset-0 flex items-center justify-center">
              <LazyLoadImage
                src={`${process.env.REACT_APP_API_BASE_URL}${product.image}`}
                alt={product.name}
                className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                onLoad={handleImageLoad}
                effect="blur"
              />
            </div>
            {/* 할인 뱃지 */}
            {getDiscountNumber(product.discount) > 0 && (
              <div className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg transform -rotate-2 hover:rotate-0 transition-transform duration-300 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
                {product.discount}% SALE
              </div>
            )}
          </div>

          {/* 상품 정보 섹션 리디자인 */}
          <div className="p-5">
            <h3 className="text-gray-900 font-semibold text-lg mobile:text-base mb-3 truncate">
              {product.name}
            </h3>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-gray-900 font-bold text-xl mobile:text-lg">
                  {t('product_card.moneyterms')} {formatNumber(product.price)}
                </p>
                <div className="flex items-center gap-1.5">
                  <TiStarFullOutline className="text-yellow-400 w-5 h-5" />
                  <span className="text-gray-600 font-medium">{product.review}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 유저 정보 */}
        <div className="flex items-center justify-between mb-3">
          <div
            className="flex items-center space-x-2 cursor-pointer pl-[10px]"
            onClick={handleProfileClick}
          >
            <div className="relative group">
              <img
                src={`${process.env.REACT_APP_API_BASE_URL}${product.profileImageUrl}`}
                alt={product.nickname}
                className="w-8 h-8 rounded-full object-cover border-2 border-gray-50"
                onMouseEnter={fetchRandomProducts}
              />
              {/* 인증 뱃지 */}
              <div className="absolute -right-1 -bottom-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full p-1">
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                </svg>
              </div>
              
              {/* 툴팁 */}
              <div 
                className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-[9999]"
              >
                <div className="bg-white text-black rounded-lg shadow-lg border border-gray-200 p-4 min-w-[250px]">
                  <div className="flex flex-col items-center gap-2 mb-4 text-center">
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL}${product.profileImageUrl}`}
                      alt={product.nickname}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-bold text-lg">{product.nickname}</p>
                      <p className="text-sm text-gray-600">해당 판매자의 정보</p>
                    </div>
                  </div>
                  
                  {/* 랜덤 상품 그리드 */}
                  <div className="grid grid-cols-2 gap-2">
                    {isLoadingRandomProducts ? (
                      <div className="col-span-2 flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                      </div>
                    ) : (
                      randomProducts.map((randomProduct) => (
                        <div key={randomProduct.id} className="relative aspect-square">
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL}${randomProduct.image}`}
                            alt={randomProduct.name}
                            className="w-full h-full object-cover rounded-md"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                            <p className="text-xs text-white truncate">{randomProduct.name}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                {/* 툴팁 화살표 - 위치 변경 */}
                <div className="w-3 h-3 bg-white border-l border-b border-gray-200 transform rotate-45 absolute -bottom-1.5 left-4"></div>
              </div>
            </div>
            <span className="text-sm font-medium text-gray-700">{product.nickname}</span>
          </div>

          {/* 더보기 버튼 */}
          <div ref={dropdownRef} className="relative">
            <button
              onClick={toggleUserDropdown}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
            
            {isMoreOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                {moreOptions.map((option, index) => (
                  <button
                    key={index}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                    onClick={() => handleOptionClick(option)}
                  >
                    {option === '신고하기' ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    )}
                    <span>{option}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 수정하기 */}
      <AllModal isOpen={isModalOpen} onClose={closeModal}>
        <h2 className="Pretendard-Bold flex items-center gap-3 text-2xl mobile:text-lg text-[#141414] border-b pb-4">
          {t('product_card.product_edit')}
        </h2>
        {/* 수정 폼 */}
        <form onSubmit={handleFormSubmit} className="mt-4">
          {/* 상품명 필드 */}
          <div className="mb-6">
            <label
              className="block Pretendard-Medium text-[#141414] leading-8 tracking-wide text-base mobile:text-sm"
              htmlFor="productName"
            >
              {t('product_card.product_name')}
            </label>
            <input
              type="text"
              id="productName"
              name="productName"
              value={productName}
              onChange={handleProductNameChange}
              className="shadow appearance-none mobile:text-[13px] mobile:mt-2 border rounded w-full py-4 px-3 text-[#1f1f1f] leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          {/* 썸네일 필드 */}
          <div className="mb-4">
            <label
              className="block Pretendard-Medium text-[#141414] tracking-wide leading-8 text-base mobile:text-sm"
              htmlFor="thumbnail"
            >
              {t('product_card.thumbnail')}
            </label>
            <input
              type="file"
              id="thumbnail"
              name="thumbnail"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="shadow appearance-none border mobile:text-[13px] mobile:mt-2 rounded w-full py-4 px-3 text-[#1f1f1f] leading-tight focus:outline-none focus:shadow-outline"
            />
            <div className="flex justify-center mt-8">
              {/* 썸네일 미리보기 */}
              {thumbnailPreview && (
                <img
                  src={thumbnailPreview}
                  alt={t('product_card.thumbnail_preview')}
                  className="w-36 h-36 object-cover rounded"
                />
              )}
            </div>
          </div>

          {/* 오류 메시지 */}
          {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}

          {/* 제출 버튼 */}
          <div className="flex justify-center mt-[42px]">
            <button
              type="button"
              onClick={closeModal}
              className="bg-white hover:bg-slate-50 transition duration-300 Pretendard-Bold ease-in-out tracking-wide text-black border px-6 py-2 rounded-lg mr-4"
            >
              {t('product_card.cancel')}
            </button>
            <button
              type="submit"
              className={`bg-black/85 hover:opacity-80 transition duration-300  Pretendard-Bold tracking-wide ease-in-out text-white px-6 py-2 rounded-lg ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('product_card.save_loading')
                : t('product_card.save')}
            </button>
          </div>
        </form>
      </AllModal>
    </>
  );
}
